import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import Header from '../components/Layout/Header';
import Modal from '../components/UI/Modal';
import StatusPill from '../components/UI/StatusPill';
import { money } from '../utils/format';
import { canViewCosts } from '../utils/permissions';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import {
  Download, Save, BookOpen, Search, Filter, ChevronUp, ChevronDown, ChevronsUpDown,
  Package, AlertTriangle, DollarSign, TrendingDown, ClipboardList, ArrowLeftRight,
  Trash2, Users, Activity, BarChart3, Truck, Archive, Eye, ShoppingCart,
  RefreshCw, X, Star, Plus, ChevronRight, Settings
} from 'lucide-react';

const COLORS = ['#4A9EBF', '#7BC47F', '#E8A04A', '#78A8E8', '#B07FD4', '#5BC4C0', '#D4A870', '#E87070'];

const REPORT_DEFS = {
  'inventory-health': {
    label: 'Inventory Health', icon: Package, category: 'Inventory',
    endpoint: '/api/reports/inventory-health',
    filters: ['location', 'category', 'supplier', 'status', 'search'],
    statusOptions: ['healthy', 'low', 'reorder', 'out_of_stock', 'overstock'],
    columns: [
      { key: 'product_name', label: 'Product', sortable: true },
      { key: 'sku', label: 'SKU', sortable: true },
      { key: 'category_name', label: 'Category', sortable: true },
      { key: 'location_name', label: 'Location', sortable: true },
      { key: 'current_stock', label: 'Stock', sortable: true, format: 'number' },
      { key: 'par_min', label: 'Min', format: 'number' },
      { key: 'reorder_point', label: 'Reorder Pt', format: 'number' },
      { key: 'reorder_qty', label: 'Reorder Qty', format: 'number' },
      { key: 'status', label: 'Status', format: 'status' },
      { key: 'unit_cost', label: 'Unit Cost', format: 'money' },
      { key: 'inventory_value', label: 'Value', sortable: true, format: 'money' },
    ],
    summaryFn: (data) => [
      { label: 'Total Items', value: data.length },
      { label: 'Healthy', value: data.filter(d => d.status === 'healthy').length, color: 'var(--success)' },
      { label: 'Low', value: data.filter(d => d.status === 'low').length, color: 'var(--warning)' },
      { label: 'Reorder', value: data.filter(d => d.status === 'reorder').length, color: 'var(--danger)' },
      { label: 'Out of Stock', value: data.filter(d => d.status === 'out_of_stock').length, color: '#E74C3C' },
      { label: 'Total Value', value: money(data.reduce((s, d) => s + Number(d.inventory_value || 0), 0)), color: 'var(--gold)' },
    ],
    chart: 'none',
  },
  'inventory-value': {
    label: 'Inventory Value', icon: DollarSign, category: 'Inventory',
    endpoint: '/api/reports/inventory-value',
    filters: ['location', 'category', 'supplier'],
    columns: [
      { key: 'product_name', label: 'Product', sortable: true },
      { key: 'sku', label: 'SKU' },
      { key: 'category_name', label: 'Category', sortable: true },
      { key: 'location_name', label: 'Location', sortable: true },
      { key: 'supplier_name', label: 'Supplier', sortable: true },
      { key: 'current_stock', label: 'Stock', format: 'number', sortable: true },
      { key: 'unit_cost', label: 'Unit Cost', format: 'money' },
      { key: 'value', label: 'Value', format: 'money', sortable: true },
    ],
    summaryFn: (data) => [
      { label: 'Total Items', value: data.length },
      { label: 'Total Value', value: money(data.reduce((s, d) => s + Number(d.value || 0), 0)), color: 'var(--gold)' },
      { label: 'Total Stock', value: Math.round(data.reduce((s, d) => s + Number(d.current_stock || 0), 0)).toLocaleString() },
    ],
    chart: 'pie', chartKey: 'value', chartNameKey: 'category_name', chartGroupBy: 'category_name',
  },
  'dead-stock': {
    label: 'Dead Stock', icon: Archive, category: 'Inventory',
    endpoint: '/api/reports/dead-stock',
    filters: ['location', 'days'],
    defaultDays: 30,
    columns: [
      { key: 'product_name', label: 'Product', sortable: true },
      { key: 'sku', label: 'SKU' },
      { key: 'category_name', label: 'Category' },
      { key: 'location_name', label: 'Location', sortable: true },
      { key: 'current_stock', label: 'Stock', format: 'number' },
      { key: 'inventory_value', label: 'Value', format: 'money', sortable: true },
      { key: 'days_since_count', label: 'Days Idle', sortable: true },
      { key: 'last_counted_at', label: 'Last Count', format: 'date' },
    ],
    summaryFn: (data) => [
      { label: 'Dead Items', value: data.length, color: 'var(--danger)' },
      { label: 'Tied Up Value', value: money(data.reduce((s, d) => s + Number(d.inventory_value || 0), 0)), color: 'var(--warning)' },
    ],
    chart: 'none',
  },
  'reorder-due': {
    label: 'Reorder / Low Stock', icon: AlertTriangle, category: 'Purchasing',
    endpoint: '/api/reports/reorder-due',
    filters: ['location'],
    columns: [
      { key: 'product_name', label: 'Product', sortable: true },
      { key: 'sku', label: 'SKU' },
      { key: 'category_name', label: 'Category' },
      { key: 'location_name', label: 'Location', sortable: true },
      { key: 'supplier_name', label: 'Supplier', sortable: true },
      { key: 'current_stock', label: 'Stock', format: 'number', sortable: true },
      { key: 'reorder_point', label: 'Reorder Pt', format: 'number' },
      { key: 'reorder_qty', label: 'Reorder Qty', format: 'number' },
      { key: 'unit_cost', label: 'Cost', format: 'money' },
    ],
    summaryFn: (data) => [
      { label: 'Items to Reorder', value: data.length, color: 'var(--danger)' },
      { label: 'Est. Reorder Cost', value: money(data.reduce((s, d) => s + (Number(d.reorder_qty || 0) * Number(d.unit_cost || 0)), 0)), color: 'var(--warning)' },
      { label: 'Out of Stock', value: data.filter(d => Number(d.current_stock) === 0).length, color: '#E74C3C' },
    ],
    actionable: true,
    chart: 'none',
  },
  'po-history': {
    label: 'PO History', icon: ClipboardList, category: 'Purchasing',
    endpoint: '/api/reports/po-history',
    filters: ['location', 'supplier', 'status', 'days'],
    statusOptions: ['draft', 'sent', 'confirmed', 'received', 'cancelled'],
    defaultDays: 90,
    columns: [
      { key: 'po_number', label: 'PO #', sortable: true },
      { key: 'supplier_name', label: 'Supplier', sortable: true },
      { key: 'location_name', label: 'Location', sortable: true },
      { key: 'status', label: 'Status', format: 'status', sortable: true },
      { key: 'total_amount', label: 'Total', format: 'money', sortable: true },
      { key: 'created_by_name', label: 'Created By' },
      { key: 'created_at', label: 'Created', format: 'date', sortable: true },
      { key: 'received_at', label: 'Received', format: 'date' },
    ],
    summaryFn: (data) => [
      { label: 'Total POs', value: data.length },
      { label: 'Total Spend', value: money(data.reduce((s, d) => s + Number(d.total_amount || 0), 0)), color: 'var(--gold)' },
      { label: 'Received', value: data.filter(d => d.status === 'received').length, color: 'var(--success)' },
      { label: 'Pending', value: data.filter(d => ['sent', 'confirmed'].includes(d.status)).length, color: 'var(--warning)' },
    ],
    chart: 'none',
  },
  'supplier-performance': {
    label: 'Supplier Performance', icon: Truck, category: 'Purchasing',
    endpoint: '/api/reports/supplier-performance',
    filters: ['days'],
    defaultDays: 90,
    columns: [
      { key: 'supplier_name', label: 'Supplier', sortable: true },
      { key: 'contact_name', label: 'Contact' },
      { key: 'po_count', label: 'POs', format: 'number', sortable: true },
      { key: 'total_spend', label: 'Total Spend', format: 'money', sortable: true },
      { key: 'received_count', label: 'Received', format: 'number' },
      { key: 'cancelled_count', label: 'Cancelled', format: 'number' },
      { key: 'avg_delivery_days', label: 'Avg Delivery (days)', sortable: true },
      { key: 'fill_rate_pct', label: 'Fill Rate %', sortable: true },
    ],
    summaryFn: (data) => [
      { label: 'Active Suppliers', value: data.filter(d => Number(d.po_count) > 0).length },
      { label: 'Total Spend', value: money(data.reduce((s, d) => s + Number(d.total_spend || 0), 0)), color: 'var(--gold)' },
      { label: 'Avg Fill Rate', value: data.filter(d => d.fill_rate_pct).length > 0 ? Math.round(data.filter(d => d.fill_rate_pct).reduce((s, d, _, a) => s + Number(d.fill_rate_pct || 0) / a.length, 0)) + '%' : 'N/A' },
    ],
    chart: 'none',
  },
  'count-variance': {
    label: 'Count Variance', icon: BarChart3, category: 'Operations',
    endpoint: '/api/reports/count-variance',
    filters: ['location', 'days', 'user'],
    defaultDays: 30,
    columns: [
      { key: 'product_name', label: 'Product', sortable: true },
      { key: 'sku', label: 'SKU' },
      { key: 'location_name', label: 'Location', sortable: true },
      { key: 'previous_qty', label: 'Expected', format: 'number' },
      { key: 'count_qty', label: 'Counted', format: 'number' },
      { key: 'variance', label: 'Variance', format: 'variance', sortable: true },
      { key: 'variance_pct', label: 'Var %', sortable: true },
      { key: 'variance_cost', label: 'Cost Impact', format: 'money', sortable: true },
      { key: 'counted_by_name', label: 'Counted By', sortable: true },
      { key: 'counted_at', label: 'Date', format: 'date', sortable: true },
    ],
    summaryFn: (data) => [
      { label: 'Total Counts', value: data.length },
      { label: 'Net Variance', value: Math.round(data.reduce((s, d) => s + Number(d.variance || 0), 0)).toLocaleString(), color: 'var(--warning)' },
      { label: 'Variance Cost', value: money(data.reduce((s, d) => s + Number(d.variance_cost || 0), 0)), color: 'var(--danger)' },
    ],
    chart: 'bar', chartKey: 'variance', chartNameKey: 'product_name', chartGroupBy: 'product_name',
  },
  'usage-trends': {
    label: 'Usage Trends', icon: TrendingDown, category: 'Operations',
    endpoint: '/api/reports/usage-trends',
    filters: ['location', 'days'],
    defaultDays: 30,
    columns: [
      { key: 'date', label: 'Date', sortable: true },
      { key: 'avg_qty', label: 'Avg Qty', format: 'number', sortable: true },
      { key: 'total_variance', label: 'Variance', format: 'variance', sortable: true },
    ],
    summaryFn: (data) => [
      { label: 'Data Points', value: data.length },
      { label: 'Avg Daily Qty', value: data.length > 0 ? (data.reduce((s, d) => s + Number(d.avg_qty || 0), 0) / data.length).toFixed(1) : '0' },
      { label: 'Total Variance', value: Math.round(data.reduce((s, d) => s + Number(d.total_variance || 0), 0)).toLocaleString(), color: 'var(--warning)' },
    ],
    chart: 'line', chartKey: 'avg_qty', chartNameKey: 'date',
  },
  'waste-detail': {
    label: 'Waste / Damage', icon: Trash2, category: 'Operations',
    endpoint: '/api/reports/waste-detail',
    filters: ['location', 'days', 'user'],
    defaultDays: 90,
    columns: [
      { key: 'product_name', label: 'Product', sortable: true },
      { key: 'sku', label: 'SKU' },
      { key: 'location_name', label: 'Location', sortable: true },
      { key: 'reason', label: 'Reason', format: 'status', sortable: true },
      { key: 'quantity', label: 'Qty Lost', format: 'number', sortable: true },
      { key: 'cost_impact', label: 'Cost Impact', format: 'money', sortable: true },
      { key: 'logged_by_name', label: 'Logged By', sortable: true },
      { key: 'logged_at', label: 'Date', format: 'date', sortable: true },
      { key: 'notes', label: 'Notes' },
    ],
    summaryFn: (data) => [
      { label: 'Incidents', value: data.length },
      { label: 'Total Qty Lost', value: Math.round(data.reduce((s, d) => s + Number(d.quantity || 0), 0)).toLocaleString(), color: 'var(--warning)' },
      { label: 'Total Cost', value: money(data.reduce((s, d) => s + Number(d.cost_impact || 0), 0)), color: 'var(--danger)' },
    ],
    chart: 'bar', chartKey: 'cost_impact', chartNameKey: 'reason', chartGroupBy: 'reason',
  },
  'transfers-report': {
    label: 'Transfers', icon: ArrowLeftRight, category: 'Transfers',
    endpoint: '/api/reports/transfers',
    filters: ['location', 'status', 'days'],
    statusOptions: ['pending', 'approved', 'in_transit', 'completed', 'cancelled'],
    defaultDays: 90,
    columns: [
      { key: 'product_name', label: 'Product', sortable: true },
      { key: 'sku', label: 'SKU' },
      { key: 'from_location', label: 'From', sortable: true },
      { key: 'to_location', label: 'To', sortable: true },
      { key: 'quantity', label: 'Qty', format: 'number', sortable: true },
      { key: 'transfer_value', label: 'Value', format: 'money', sortable: true },
      { key: 'status', label: 'Status', format: 'status', sortable: true },
      { key: 'requested_by_name', label: 'Requested By' },
      { key: 'approved_by_name', label: 'Approved By' },
      { key: 'requested_at', label: 'Date', format: 'date', sortable: true },
    ],
    summaryFn: (data) => [
      { label: 'Total Transfers', value: data.length },
      { label: 'Total Value', value: money(data.reduce((s, d) => s + Number(d.transfer_value || 0), 0)), color: 'var(--gold)' },
      { label: 'Completed', value: data.filter(d => d.status === 'completed').length, color: 'var(--success)' },
      { label: 'Pending', value: data.filter(d => ['pending', 'approved'].includes(d.status)).length, color: 'var(--warning)' },
    ],
    chart: 'none',
  },
  'user-activity': {
    label: 'User Activity', icon: Activity, category: 'Activity',
    endpoint: '/api/reports/user-activity',
    filters: ['location', 'days', 'user'],
    defaultDays: 30,
    columns: [
      { key: 'user_name', label: 'User', sortable: true },
      { key: 'action_type', label: 'Action', format: 'status', sortable: true },
      { key: 'description', label: 'Description' },
      { key: 'location_name', label: 'Location', sortable: true },
      { key: 'detail', label: 'Detail' },
      { key: 'action_date', label: 'Date', format: 'date', sortable: true },
    ],
    summaryFn: (data) => [
      { label: 'Total Actions', value: data.length },
      { label: 'Counts', value: data.filter(d => d.action_type === 'count').length },
      { label: 'Orders', value: data.filter(d => d.action_type === 'order').length },
      { label: 'Transfers', value: data.filter(d => d.action_type === 'transfer').length },
      { label: 'Waste Logs', value: data.filter(d => d.action_type === 'waste').length },
    ],
    chart: 'none',
  },
  'top-products': {
    label: 'Top Movers', icon: TrendingDown, category: 'Activity',
    endpoint: '/api/reports/top-products',
    filters: ['location', 'days'],
    defaultDays: 30,
    columns: [
      { key: 'name', label: 'Product', sortable: true },
      { key: 'sku', label: 'SKU' },
      { key: 'category_name', label: 'Category' },
      { key: 'count_frequency', label: 'Count Freq', format: 'number', sortable: true },
      { key: 'total_movement', label: 'Total Movement', format: 'number', sortable: true },
    ],
    summaryFn: (data) => [
      { label: 'Products Tracked', value: data.length },
      { label: 'Total Movement', value: Math.round(data.reduce((s, d) => s + Number(d.total_movement || 0), 0)).toLocaleString() },
    ],
    chart: 'bar', chartKey: 'total_movement', chartNameKey: 'name',
  },
};

const REPORT_CATEGORIES = [
  { key: 'Inventory', icon: Package },
  { key: 'Purchasing', icon: ShoppingCart },
  { key: 'Operations', icon: BarChart3 },
  { key: 'Transfers', icon: ArrowLeftRight },
  { key: 'Activity', icon: Activity },
];

const CUSTOM_SOURCES = [
  { value: 'inventory', label: 'Inventory' },
  { value: 'orders', label: 'Purchase Orders' },
  { value: 'transfers', label: 'Transfers' },
  { value: 'counts', label: 'Inventory Counts' },
  { value: 'waste', label: 'Waste Log' },
  { value: 'suppliers', label: 'Suppliers' },
];

export default function Reports() {
  const { user } = useAuth();
  const nav = useNavigate();
  const showCosts = canViewCosts(user);

  // State
  const [mode, setMode] = useState('prebuilt');
  const [activeReport, setActiveReport] = useState('inventory-health');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [usersList, setUsersList] = useState([]);

  // Filters
  const [locationId, setLocationId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [days, setDays] = useState(30);
  const [userId, setUserId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Sort
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // Chart
  const [showChart, setShowChart] = useState(false);

  // Saved reports
  const [savedReports, setSavedReports] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sp_saved_reports') || '[]'); } catch { return []; }
  });
  const [saveModal, setSaveModal] = useState(false);
  const [saveName, setSaveName] = useState('');

  // Custom builder
  const [customSource, setCustomSource] = useState('inventory');
  const [customSortBy, setCustomSortBy] = useState('');
  const [customSortDir, setCustomSortDir] = useState('asc');

  // Load reference data
  useEffect(() => {
    Promise.all([
      api('/api/locations').catch(() => ({ data: [] })),
      api('/api/categories').catch(() => ({ data: [] })),
      api('/api/suppliers').catch(() => ({ data: [] })),
      api('/api/users').catch(() => ({ data: [] })),
    ]).then(([l, c, s, u]) => {
      setLocations(l.data || []);
      setCategories(c.data || []);
      setSuppliers(s.data || []);
      setUsersList(u.data || []);
    });
  }, []);

  // Reset filters when switching reports
  const resetFilters = useCallback((def) => {
    setLocationId('');
    setCategoryId('');
    setSupplierId('');
    setStatusFilter('');
    setUserId('');
    setSearchTerm('');
    setSortCol(null);
    setSortDir('asc');
    setDays(def && def.defaultDays ? def.defaultDays : 30);
  }, []);

  // Load report data
  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      if (mode === 'custom') {
        const p = new URLSearchParams({ source: customSource });
        if (locationId) p.set('location_id', locationId);
        if (categoryId) p.set('category_id', categoryId);
        if (supplierId) p.set('supplier_id', supplierId);
        if (statusFilter) p.set('status', statusFilter);
        if (days) p.set('days', days);
        if (userId) p.set('user_id', userId);
        if (customSortBy) { p.set('sort_by', customSortBy); p.set('sort_dir', customSortDir); }
        const r = await api('/api/reports/custom?' + p);
        setData(r.data || []);
      } else if (mode === 'prebuilt') {
        const def = REPORT_DEFS[activeReport];
        if (!def) { setData([]); setLoading(false); return; }
        const p = new URLSearchParams();
        if (locationId && def.filters.includes('location')) p.set('location_id', locationId);
        if (categoryId && def.filters.includes('category')) p.set('category_id', categoryId);
        if (supplierId && def.filters.includes('supplier')) p.set('supplier_id', supplierId);
        if (statusFilter && def.filters.includes('status')) p.set('status', statusFilter);
        if (days && def.filters.includes('days')) p.set('days', days);
        if (userId && def.filters.includes('user')) p.set('user_id', userId);
        if (searchTerm && def.filters.includes('search')) p.set('search', searchTerm);
        const r = await api(def.endpoint + '?' + p);
        setData(r.data || []);
      }
    } catch (e) {
      console.error('Report load error:', e);
      setData([]);
    }
    setLoading(false);
  }, [activeReport, mode, locationId, categoryId, supplierId, statusFilter, days, userId, searchTerm, customSource, customSortBy, customSortDir]);

  useEffect(() => {
    if (mode !== 'saved') loadReport();
  }, [loadReport, mode]);

  // Sorting
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const sorted = useMemo(() => {
    if (!sortCol || !data.length) return data;
    return [...data].sort((a, b) => {
      const va = a[sortCol], vb = b[sortCol];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const isNum = !isNaN(Number(va)) && !isNaN(Number(vb));
      const cmp = isNum ? Number(va) - Number(vb) : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortCol, sortDir]);

  // Current report def
  const def = mode === 'prebuilt' ? REPORT_DEFS[activeReport] : null;

  // Columns for current view
  const columns = useMemo(() => {
    if (mode === 'custom' && data.length > 0) {
      return Object.keys(data[0]).filter(k => !k.includes('_id') || k === 'id').map(k => ({
        key: k,
        label: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        sortable: true,
        format: k.includes('cost') || k.includes('value') || k.includes('total') || k.includes('amount') || k.includes('price') ? 'money'
          : k.includes('date') || k.includes('_at') || k.includes('created') ? 'date'
          : k === 'status' || k === 'reason' ? 'status'
          : undefined,
      }));
    }
    return def ? def.columns : [];
  }, [mode, def, data]);

  // Summary cards
  const summaryCards = useMemo(() => {
    if (!data.length) return [];
    let cards = [];
    if (mode === 'prebuilt' && def && def.summaryFn) cards = def.summaryFn(data);
    else if (mode === 'custom') cards = [{ label: 'Total Rows', value: data.length }];
    // Hide monetary values if user can't see costs
    if (!showCosts) {
      const moneyLabels = ['value', 'cost', 'spend', 'price'];
      cards = cards.map(c => {
        const isMonetary = moneyLabels.some(k => c.label.toLowerCase().includes(k)) || String(c.value).startsWith('$');
        return isMonetary ? { ...c, value: '•••' } : c;
      });
    }
    return cards;
  }, [data, mode, def, showCosts]);

  // Chart data
  const chartData = useMemo(() => {
    if (!def || !def.chart || def.chart === 'none' || !data.length) return [];
    if (def.chart === 'line') return data;
    if (def.chartGroupBy) {
      const grouped = {};
      data.forEach(row => {
        const key = row[def.chartGroupBy] || 'Unknown';
        if (!grouped[key]) grouped[key] = { name: key, value: 0 };
        grouped[key].value += Number(row[def.chartKey] || 0);
      });
      return Object.values(grouped);
    }
    return data.slice(0, 15).map(row => ({
      name: String(row[def.chartNameKey] || '').substring(0, 20),
      value: Number(row[def.chartKey] || 0),
    }));
  }, [data, def]);

  // Format cell value
  const formatCell = (value, format) => {
    if (value == null || value === '') return '\u2014';
    switch (format) {
      case 'money': return showCosts ? money(value) : '•••';
      case 'number': return Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 });
      case 'date': {
        const d = new Date(value);
        return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
      }
      case 'status': return <StatusPill status={String(value)} />;
      case 'variance': {
        const n = Number(value);
        return <span style={{ color: n < 0 ? 'var(--danger)' : n > 0 ? 'var(--success)' : 'var(--muted)', fontWeight: 600 }}>{n > 0 ? '+' : ''}{n.toFixed(1)}</span>;
      }
      default: return String(value);
    }
  };

  // CSV export
  const exportCSV = () => {
    if (!data.length) return;
    const cols = columns.map(c => c.key);
    const labels = columns.map(c => c.label);
    const csv = [labels.join(','), ...data.map(row => cols.map(k => JSON.stringify(row[k] ?? '')).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (activeReport || customSource) + '-report.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Saved reports
  const saveReport = () => {
    if (!saveName.trim()) return;
    const config = {
      name: saveName,
      report: activeReport,
      mode: mode === 'saved' ? 'prebuilt' : mode,
      filters: { locationId, categoryId, supplierId, statusFilter, days, userId, searchTerm },
      customSource,
      savedAt: new Date().toISOString(),
    };
    const updated = [...savedReports, config];
    setSavedReports(updated);
    localStorage.setItem('sp_saved_reports', JSON.stringify(updated));
    setSaveModal(false);
    setSaveName('');
  };

  const loadSavedReport = (config) => {
    setMode(config.mode || 'prebuilt');
    setActiveReport(config.report || 'inventory-health');
    if (config.filters) {
      setLocationId(config.filters.locationId || '');
      setCategoryId(config.filters.categoryId || '');
      setSupplierId(config.filters.supplierId || '');
      setStatusFilter(config.filters.statusFilter || '');
      setDays(config.filters.days || 30);
      setUserId(config.filters.userId || '');
      setSearchTerm(config.filters.searchTerm || '');
    }
    if (config.customSource) setCustomSource(config.customSource);
  };

  const deleteSavedReport = (idx) => {
    const updated = savedReports.filter((_, i) => i !== idx);
    setSavedReports(updated);
    localStorage.setItem('sp_saved_reports', JSON.stringify(updated));
  };

  // Sort icon component
  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronsUpDown size={11} style={{ opacity: .3, marginLeft: 3 }} />;
    return sortDir === 'asc'
      ? <ChevronUp size={11} style={{ color: 'var(--gold)', marginLeft: 3 }} />
      : <ChevronDown size={11} style={{ color: 'var(--gold)', marginLeft: 3 }} />;
  };

  // Determine which filters to show
  const activeFilters = mode === 'custom'
    ? ['location', 'category', 'supplier', 'status', 'days', 'user']
    : (def ? def.filters : []);

  // Chart rendering
  const renderChart = () => {
    if (!def || !def.chart || def.chart === 'none' || !chartData.length) return null;

    const tooltipStyle = { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 };

    if (def.chart === 'pie') {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110}
              label={({ name, percent }) => name.substring(0, 12) + ' ' + (percent * 100).toFixed(0) + '%'}>
              {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} formatter={v => showCosts ? money(v) : '•••'} />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    if (def.chart === 'bar') {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ bottom: 30 }}>
            <XAxis dataKey="name" tick={{ fill: 'var(--muted)', fontSize: 10 }} angle={-35} textAnchor="end" height={70} interval={0} />
            <YAxis tick={{ fill: 'var(--muted)', fontSize: 10 }} />
            <Tooltip contentStyle={tooltipStyle} formatter={v => typeof v === 'number' && def.chartKey.includes('cost') ? (showCosts ? money(v) : '•••') : Number(v).toLocaleString()} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (def.chart === 'line') {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <XAxis dataKey={def.chartNameKey || 'date'} tick={{ fill: 'var(--muted)', fontSize: 10 }} />
            <YAxis tick={{ fill: 'var(--muted)', fontSize: 10 }} />
            <Tooltip contentStyle={tooltipStyle} />
            <Line type="monotone" dataKey={def.chartKey || 'avg_qty'} stroke="var(--gold)" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    return null;
  };

  // Mode button style helper
  const modeBtn = (m, label, icon) => {
    const Icon = icon;
    const active = mode === m;
    return (
      <button
        onClick={() => { setMode(m); if (m === 'prebuilt') resetFilters(REPORT_DEFS[activeReport]); }}
        style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          padding: '7px 6px', borderRadius: 6, border: 'none', cursor: 'pointer',
          background: active ? 'var(--gold)' : 'transparent',
          color: active ? 'var(--navy)' : 'var(--muted)',
          fontSize: 11, fontWeight: active ? 700 : 500,
          fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s',
        }}
      >
        <Icon size={12} />
        {label}
      </button>
    );
  };

  return (
    <div style={{ display: 'flex', gap: 0, minHeight: 'calc(100vh - 80px)' }}>
      {/* LEFT: Report Navigation Sidebar */}
      <div style={{
        width: 240, flexShrink: 0, background: 'var(--card)',
        borderRight: '1px solid var(--border)', borderRadius: '14px 0 0 14px',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
      }}>
        {/* Mode toggle */}
        <div style={{ padding: '16px 14px 12px', borderBottom: '1px solid var(--border)' }}>
          <h2 style={{ fontFamily: 'Lora, serif', fontSize: 16, margin: '0 0 12px', color: 'var(--gold)' }}>Reports</h2>
          <div style={{ display: 'flex', gap: 3, background: 'var(--navy)', borderRadius: 8, padding: 3 }}>
            {modeBtn('prebuilt', 'Prebuilt', BookOpen)}
            {modeBtn('custom', 'Custom', Settings)}
            {modeBtn('saved', 'Saved', Star)}
          </div>
        </div>

        {/* Report list or saved reports */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {mode === 'prebuilt' && REPORT_CATEGORIES.map(cat => {
            const CatIcon = cat.icon;
            const reports = Object.entries(REPORT_DEFS).filter(([_, d]) => d.category === cat.key);
            if (!reports.length) return null;
            return (
              <div key={cat.key}>
                <div style={{
                  padding: '10px 14px 6px', fontSize: 10, fontWeight: 700,
                  color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '1px',
                  display: 'flex', alignItems: 'center', gap: 5,
                }}>
                  <CatIcon size={10} />
                  {cat.key}
                </div>
                {reports.map(([key, rDef]) => {
                  const RIcon = rDef.icon;
                  const isActive = activeReport === key && mode === 'prebuilt';
                  return (
                    <button
                      key={key}
                      onClick={() => { setActiveReport(key); setMode('prebuilt'); resetFilters(rDef); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 14px',
                        background: isActive ? 'var(--gold-pale)' : 'transparent',
                        color: isActive ? 'var(--gold)' : 'var(--muted)',
                        border: 'none',
                        borderLeft: isActive ? '3px solid var(--gold)' : '3px solid transparent',
                        cursor: 'pointer', fontSize: 13,
                        fontWeight: isActive ? 600 : 400, textAlign: 'left',
                        fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s',
                      }}
                    >
                      <RIcon size={15} />
                      {rDef.label}
                    </button>
                  );
                })}
              </div>
            );
          })}

          {mode === 'custom' && (
            <div style={{ padding: 14 }}>
              <div style={{ fontSize: 11, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700, marginBottom: 10 }}>
                Data Source
              </div>
              {CUSTOM_SOURCES.map(src => (
                <button
                  key={src.value}
                  onClick={() => setCustomSource(src.value)}
                  style={{
                    display: 'block', width: '100%', padding: '9px 12px', marginBottom: 4,
                    background: customSource === src.value ? 'var(--gold-pale)' : 'transparent',
                    color: customSource === src.value ? 'var(--gold)' : 'var(--muted)',
                    border: 'none',
                    borderLeft: customSource === src.value ? '3px solid var(--gold)' : '3px solid transparent',
                    cursor: 'pointer', fontSize: 13, textAlign: 'left',
                    fontFamily: 'DM Sans, sans-serif', borderRadius: 0, transition: 'all 0.15s',
                    fontWeight: customSource === src.value ? 600 : 400,
                  }}
                >
                  {src.label}
                </button>
              ))}
              <div style={{ marginTop: 16, fontSize: 11, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700, marginBottom: 8 }}>
                Sort By
              </div>
              <input
                value={customSortBy}
                onChange={e => setCustomSortBy(e.target.value)}
                placeholder="Column name..."
                style={{ width: '100%', marginBottom: 6, fontSize: 12, padding: '7px 10px' }}
              />
              <select value={customSortDir} onChange={e => setCustomSortDir(e.target.value)} style={{ width: '100%', fontSize: 12, padding: '7px 10px' }}>
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          )}

          {mode === 'saved' && (
            <div style={{ padding: 14 }}>
              {savedReports.length === 0 ? (
                <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--faint)', fontSize: 13 }}>
                  <Star size={24} style={{ opacity: 0.3, marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
                  No saved reports yet. Run a report and click Save.
                </div>
              ) : savedReports.map((sr, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '10px 12px', marginBottom: 6, background: 'var(--navy)',
                    border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                  onClick={() => loadSavedReport(sr)}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.background = 'var(--gold-pale)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--navy)'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{sr.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--faint)' }}>
                        {sr.mode === 'custom' ? 'Custom: ' + sr.customSource : (REPORT_DEFS[sr.report] ? REPORT_DEFS[sr.report].label : sr.report)}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 2 }}>
                        {new Date(sr.savedAt).toLocaleDateString()}
                      </div>
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); deleteSavedReport(idx); }}
                      style={{
                        background: 'none', border: 'none', color: 'var(--faint)',
                        cursor: 'pointer', padding: 4, borderRadius: 4,
                      }}
                      title="Delete saved report"
                    >
                      <X size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT: Main Content */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Report Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 24px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', gap: 10,
        }}>
          <div>
            <h1 style={{ fontFamily: 'Lora, serif', fontSize: 20, margin: 0 }}>
              {mode === 'custom' ? 'Custom Report Builder'
                : mode === 'saved' ? 'Saved Reports'
                : def ? def.label : 'Report'}
            </h1>
            <div style={{ fontSize: 12, color: 'var(--faint)', marginTop: 2 }}>
              {sorted.length} result{sorted.length !== 1 ? 's' : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {def && def.actionable && (
              <button className="btn btn-primary btn-sm" onClick={() => nav('/orders?create=true')}>
                <ShoppingCart size={14} /> Generate POs
              </button>
            )}
            {def && def.chart && def.chart !== 'none' && chartData.length > 0 && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowChart(v => !v)}
                style={showChart ? { borderColor: 'var(--gold)', color: 'var(--gold)' } : {}}
              >
                <BarChart3 size={14} /> {showChart ? 'Hide Chart' : 'Show Chart'}
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={() => setSaveModal(true)}>
              <Save size={14} /> Save
            </button>
            <button className="btn btn-secondary btn-sm" onClick={exportCSV} disabled={!data.length}>
              <Download size={14} /> CSV
            </button>
            <button className="btn btn-secondary btn-sm" onClick={loadReport} title="Refresh">
              <RefreshCw size={14} style={loading ? { animation: 'spin 0.6s linear infinite' } : {}} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{
          padding: '12px 24px', borderBottom: '1px solid var(--border)',
          display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
        }}>
          {activeFilters.includes('location') && (
            <select value={locationId} onChange={e => setLocationId(e.target.value)} style={{ minWidth: 150, fontSize: 13, padding: '7px 10px' }}>
              <option value="">All Locations</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
          {activeFilters.includes('category') && (
            <select value={categoryId} onChange={e => setCategoryId(e.target.value)} style={{ minWidth: 130, fontSize: 13, padding: '7px 10px' }}>
              <option value="">All Categories</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          {activeFilters.includes('supplier') && (
            <select value={supplierId} onChange={e => setSupplierId(e.target.value)} style={{ minWidth: 140, fontSize: 13, padding: '7px 10px' }}>
              <option value="">All Suppliers</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          {activeFilters.includes('status') && (
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ minWidth: 120, fontSize: 13, padding: '7px 10px' }}>
              <option value="">All Statuses</option>
              {(def && def.statusOptions ? def.statusOptions : ['draft', 'sent', 'confirmed', 'received', 'cancelled', 'pending', 'approved', 'completed', 'healthy', 'low', 'reorder', 'out_of_stock']).map(s => (
                <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
              ))}
            </select>
          )}
          {activeFilters.includes('days') && (
            <select value={days} onChange={e => setDays(e.target.value)} style={{ minWidth: 120, fontSize: 13, padding: '7px 10px' }}>
              <option value="7">Last 7 days</option>
              <option value="14">Last 14 days</option>
              <option value="30">Last 30 days</option>
              <option value="60">Last 60 days</option>
              <option value="90">Last 90 days</option>
              <option value="180">Last 180 days</option>
              <option value="365">Last year</option>
            </select>
          )}
          {activeFilters.includes('user') && (
            <select value={userId} onChange={e => setUserId(e.target.value)} style={{ minWidth: 130, fontSize: 13, padding: '7px 10px' }}>
              <option value="">All Users</option>
              {usersList.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          {activeFilters.includes('search') && (
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--faint)' }} />
              <input
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search products..."
                style={{ paddingLeft: 28, fontSize: 13, padding: '7px 10px 7px 28px', minWidth: 160 }}
              />
            </div>
          )}
          {(locationId || categoryId || supplierId || statusFilter || userId || searchTerm) && (
            <button
              onClick={() => resetFilters(def)}
              style={{
                background: 'none', border: 'none', color: 'var(--faint)',
                cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4,
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              <X size={12} /> Clear filters
            </button>
          )}
        </div>

        {/* Scrollable content area */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Summary Cards */}
          {summaryCards.length > 0 && (
            <div style={{ display: 'flex', gap: 12, padding: '16px 24px', overflowX: 'auto' }}>
              {summaryCards.map((card, i) => (
                <div key={i} style={{
                  background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
                  padding: '14px 20px', minWidth: 130, textAlign: 'center', flexShrink: 0,
                }}>
                  <div style={{
                    fontSize: 22, fontWeight: 700,
                    color: card.color || 'var(--white)',
                    fontFamily: 'DM Sans, sans-serif',
                  }}>{card.value}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{card.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Chart */}
          {showChart && def && def.chart && def.chart !== 'none' && chartData.length > 0 && (
            <div style={{ padding: '0 24px 16px' }}>
              <div className="card" style={{ padding: 20 }}>
                <h3 style={{ fontFamily: 'Lora, serif', fontSize: 15, marginBottom: 14, color: 'var(--gold)' }}>
                  {def.label} Chart
                </h3>
                {renderChart()}
              </div>
            </div>
          )}

          {/* Results Table */}
          <div style={{ padding: '0 24px 24px' }}>
            {loading ? (
              <div style={{ padding: 60, textAlign: 'center', color: 'var(--muted)' }}>
                <RefreshCw size={24} style={{ color: 'var(--gold)', animation: 'spin 1s linear infinite' }} />
                <div style={{ marginTop: 10, fontSize: 13 }}>Loading report...</div>
              </div>
            ) : mode === 'saved' && data.length === 0 ? (
              <div style={{ padding: 60, textAlign: 'center', color: 'var(--muted)' }}>
                <Star size={32} style={{ opacity: 0.25, marginBottom: 10 }} />
                <div style={{ fontSize: 14 }}>Select a saved report from the sidebar to run it</div>
              </div>
            ) : sorted.length === 0 ? (
              <div style={{ padding: 60, textAlign: 'center', color: 'var(--muted)' }}>
                <Package size={32} style={{ opacity: 0.25, marginBottom: 10 }} />
                <div style={{ fontSize: 14 }}>No data for this report</div>
                <div style={{ fontSize: 12, color: 'var(--faint)', marginTop: 4 }}>Try adjusting your filters or date range</div>
              </div>
            ) : columns.length > 0 ? (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      {columns.map(col => (
                        <th
                          key={col.key}
                          onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                          style={{ cursor: col.sortable ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {col.label}
                            {col.sortable && <SortIcon col={col.key} />}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((row, i) => (
                      <tr key={i}>
                        {columns.map(col => (
                          <td key={col.key}>{formatCell(row[col.key], col.format)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: 60, textAlign: 'center', color: 'var(--muted)' }}>
                <Filter size={32} style={{ opacity: 0.25, marginBottom: 10 }} />
                <div style={{ fontSize: 14 }}>Select a report to get started</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save Modal */}
      {saveModal && (
        <Modal title="Save Report" onClose={() => setSaveModal(false)}>
          <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--navy)', borderRadius: 8, border: '1px solid var(--border)', fontSize: 12, color: 'var(--muted)' }}>
            <strong style={{ color: 'var(--gold)' }}>
              {mode === 'custom' ? 'Custom: ' + CUSTOM_SOURCES.find(s => s.value === customSource)?.label
                : def ? def.label : 'Report'}
            </strong>
            {locationId && <span> &middot; {locations.find(l => String(l.id) === locationId)?.name || 'Location ' + locationId}</span>}
            {days && activeFilters.includes('days') && <span> &middot; {days} days</span>}
          </div>
          <div className="form-group">
            <label>Report Name</label>
            <input
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
              placeholder="e.g. Weekly Low Stock Check"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter' && saveName.trim()) saveReport(); }}
            />
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setSaveModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveReport} disabled={!saveName.trim()}>
              <Save size={14} /> Save Report
            </button>
          </div>
        </Modal>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 768px) {
          [style*="width: 240"] { display: none !important; }
        }
      `}</style>
    </div>
  );
}
